import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation } from "urql";
import { InterviewType } from "../../graphql/generated/graphql";
import { DeleteTermMutation } from "../../graphql/terms.graphql";
import type { Term } from "../../hooks";
import { toUserFacingMessage } from "../../lib/graphqlErrorMessage";
import { Button } from "../ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";

const SETTING_NAMES_DELETED_WITH_TERM: Record<InterviewType, string> = {
  [InterviewType.Employee]:
    "日程調整設定、スポンサー向けリマインドメールの設定、インタビューテンプレート、インタビュアーのアサイン",
  [InterviewType.Stakeholder]:
    "インタビュー設定、日程調整設定、インタビュアーのアサイン、レポート設定",
};

interface DeleteTermSectionProps {
  term: Pick<Term, "termId" | "sponsorId" | "title" | "interviewType">;
  /** タームを削除できない理由。削除できるときは null */
  blockedReason: string | null;
}

export function DeleteTermSection({
  term,
  blockedReason,
}: DeleteTermSectionProps) {
  const navigate = useNavigate();
  const [confirming, setConfirming] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [{ fetching }, executeDeleteTerm] = useMutation(DeleteTermMutation);

  const handleDelete = async () => {
    setErrorMessage(null);
    // deleteTerm は Boolean を返すため、ターム一覧のキャッシュを無効化する型名を明示する
    const result = await executeDeleteTerm(
      { termId: term.termId },
      { additionalTypenames: ["Term"] },
    );

    if (result.error) {
      setErrorMessage(
        toUserFacingMessage(
          result.error,
          "タームの削除に失敗しました。時間をおいて再度お試しください。",
        ),
      );
      return;
    }

    setConfirming(false);
    navigate(`/employee_interview/sponsors/${term.sponsorId}`);
  };

  const handleOpenChange = (open: boolean) => {
    // 送信中に閉じるとエラー表示先が失われ、失敗が利用者に伝わらなくなる
    if (fetching) return;
    setConfirming(open);
    if (!open) setErrorMessage(null);
  };

  return (
    <div className="overflow-hidden rounded-xl border border-[var(--color-border-default)] bg-white shadow-[0_2px_8px_rgba(0,0,0,0.05)]">
      <div className="border-[var(--color-primary-500)] border-b-2 bg-[var(--color-bg-subtle-gray)] px-6 py-5">
        <h2 className="m-0 font-bold text-2xl text-[var(--color-primary-500)]">
          タームの削除
        </h2>
      </div>
      <div className="flex items-center justify-between gap-4 p-6">
        <p className="m-0 text-[var(--color-text-secondary)] text-sm">
          {blockedReason ??
            "タームと、タームに登録した設定を削除します。削除したタームは元に戻せません。"}
        </p>
        <Button
          variant="destructive"
          size="sm"
          onClick={() => setConfirming(true)}
          disabled={blockedReason !== null}
          className="shrink-0"
        >
          タームを削除
        </Button>
      </div>
      <Dialog open={confirming} onOpenChange={handleOpenChange}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>タームを削除しますか？</DialogTitle>
            <DialogDescription>
              「{term.title}」を削除します。
              {SETTING_NAMES_DELETED_WITH_TERM[term.interviewType]}
              も一緒に削除します。削除したタームは元に戻せません。
            </DialogDescription>
          </DialogHeader>
          {errorMessage && (
            <p className="text-[var(--color-red-500)] text-sm">
              {errorMessage}
            </p>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => handleOpenChange(false)}
              disabled={fetching}
            >
              閉じる
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={fetching}
            >
              {fetching ? "削除中..." : "タームを削除する"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
